import { useMatches } from "@remix-run/react";
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  guessFullScreenLayout,
  resolveFullScreenLayout,
} from "./full-screen-layout-guess";

type FullScreenLayoutState = {
  /** What the mounted page reported; `undefined` until it did. */
  requested?: boolean;
  request: Dispatch<SetStateAction<boolean | undefined>>;
};

const FullScreenLayoutContext = createContext<FullScreenLayoutState>({
  request: () => undefined,
});

/**
 * Holds the full-screen request of the page on screen. Per render tree
 * (React state), so nothing is shared between server requests.
 */
export function FullScreenLayoutProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [requested, request] = useState<boolean>();
  const value = useMemo(() => ({ requested, request }), [requested]);

  return (
    <FullScreenLayoutContext.Provider value={value}>
      {children}
    </FullScreenLayoutContext.Provider>
  );
}

/**
 * Whether the site chrome (header, navigation, footer, newsletter, floating
 * prompts) is hidden right now.
 */
export function useIsFullScreenLayout(): boolean {
  const { requested } = useContext(FullScreenLayoutContext);
  const matches = useMatches();

  return resolveFullScreenLayout(requested, guessFullScreenLayout(matches));
}

/**
 * Called by a page to hide the site chrome while `isActive`. Runs in an
 * effect only (never on the server) and gives the layout back on unmount.
 *
 * @example
 * useFullScreenLayout(isAttemptInProgress);
 */
export function useFullScreenLayout(isActive: boolean) {
  const { request } = useContext(FullScreenLayoutContext);

  useEffect(() => {
    request(isActive);

    return () => request(undefined);
  }, [isActive, request]);
}

/** Renders its children only while the normal site layout is shown. */
export function HideInFullScreenLayout({ children }: { children: ReactNode }) {
  return useIsFullScreenLayout() ? null : <>{children}</>;
}
